import { TimelineDesk } from "./TimelineDesk";
import { TimelineTask } from "./TimelineTask";
import { TimelineNotas } from "@/components/Notas/TimelineNotas";
import { IconBullet } from "../Icons";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import IaService from "@/services/serviceApi";
import { toast } from "react-toastify";
import { IconStarIa } from "@/assets/images/Icons";
import { Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { timelineItemBelongsToDepartamento } from "@/utils/timelineDepartamento";

export function Timeline({
  timeline,
  loading,
  dadosDesk,
  departamentos,
  departamentoActive,
}: any) {
  const { t } = useTranslation();
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [resumoTimeline, setResumoTimeline] = useState(null);

  function ordenarPorDataDeCriacao(array: any) {
    return array?.sort((a: any, b: any) => {
      const dataA: any = moment(a.createdAt);
      const dataB: any = moment(b.createdAt);
      return dataB - dataA;
    });
  }

  const handleGerarResumo = async () => {
    setLoadingResumo(true);
    try {
      const res = await IaService.gerarResumo({
        events: {
          dadosDaDesk: limparDadosDesk(),
          timeline: limparObjeto(),
        },
      });

      if (res.status === 200) {
        setResumoTimeline(res?.data?.summary);
      }
    } catch (error) {
      toast.error(t("userGoOn:timeline.errors.generate_summary"));
    } finally {
      setLoadingResumo(false);
    }
  };

  function limparDadosDesk() {
    return {
      name: dadosDesk.name,
      createdAt: dadosDesk.createdAt,
      date: dadosDesk.date,
      title: dadosDesk.title,
      updatedAt: dadosDesk.updatedAt,
      type: dadosDesk.type?.name,
      agency: {
        name: dadosDesk.agency?.name,
      },
      cliente: {
        name: dadosDesk.cliente?.cliente,
      },
      protagonista: {
        name: dadosDesk.protagonista?.name,
        last_name: dadosDesk.protagonista?.last_name,
        phone_number: dadosDesk.protagonista?.phone_number,
        email: dadosDesk.protagonista?.email,
        cargos: dadosDesk.protagonista?.cargos?.name,
      },
      representado: {
        name: dadosDesk.representado?.name,
      },
      representante: {
        name: dadosDesk.representante?.name,
      },
      responsavel: {
        name: dadosDesk.responsavel?.name,
        last_name: dadosDesk.responsavel?.last_name,
        phone_number: dadosDesk.responsavel?.phone_number,
        email: dadosDesk.responsavel?.email,
      },
      user: {
        name: dadosDesk.user?.name,
        last_name: dadosDesk.user?.last_name,
        phone_number: dadosDesk.user?.phone_number,
        email: dadosDesk.user?.email,
      },
      vaiculo: dadosDesk.veiculo?.name,
    };
  }

  function limparObjeto(): any {
    const limpaTimeline: any = [];
    timeline.data.map((e: any) => {
      const data = {
        alteracao: e.alteracao,
        createdAt: e.createdAt,
        new: e.new,
        old: e.old,
        reuniao: e.reuniao,
        tarefa: e.tarefa,
        type: e.type,
        user: {
          name: e.user?.name,
          last_name: e.user?.last_name,
          email: e.user?.email,
          linkedin: e.user?.linkedin,
          is_admin: e.user?.is_admin,
          phone_number: e.user?.phone_number,
        },
      };
      limpaTimeline.push(data);
    });

    return limpaTimeline;
  }

  const AnimatedText = ({ text, speed }: any) => {
    const [displayedText, setDisplayedText] = useState<string>("");
    const words = useMemo(
      () => (typeof text === "string" ? text.split(" ") : []),
      [text]
    );

    useEffect(() => {
      let index = 0;
      setDisplayedText("");
      const interval = setInterval(() => {
        if (index < words.length) {
          setDisplayedText(words.slice(0, index + 1).join(" "));
          index++;
        } else {
          clearInterval(interval);
        }
      }, speed);

      return () => clearInterval(interval);
    }, [words, speed]);

    return <p>{displayedText}</p>;
  };

  return (
    <div>
      <div className="mx-auto max-w-md relative pb-4 space-y-4">
        <div className="flex justify-center items-center w-full">
          <button
            onClick={handleGerarResumo}
            className="buttonResumoIA flex justify-center items-center text-center text-white text-xs px-8 py-2 rounded-full gap-x-4 relative"
          >
            {t("userGoOn:timeline.generate_summary")}
            <IconStarIa />
            <div className="absolute -top-2 right-0">
              <div className="relative group">
                <button
                  className="p-1 bg-black hover:text-azul-400 rounded-full shadow-md text-white transition-colors duration-300"
                  aria-label={t("userGoOn:timeline.information")}
                >
                  <Info className="w-3 h-3" />
                </button>
                <div className="absolute top-11 right-0 hidden group-hover:block bg-gray-700 text-white text-xs rounded-md p-2 shadow-lg w-56">
                  {t("userGoOn:timeline.test_functionality_warning")}
                </div>
              </div>
            </div>
          </button>
        </div>
        <div className="w-full">
          {!!loadingResumo && (
            <div className="resumoIa w-full rounded-[24px] p-4">
              <div className="animate-pulse text-white text-center">
                {t("userGoOn:timeline.loading_summary")}
              </div>
            </div>
          )}
          {!loadingResumo && !!resumoTimeline && (
            <div className="text-white resumoIa w-full rounded-[24px] p-4">
              <AnimatedText text={resumoTimeline} speed={50} />
            </div>
          )}
        </div>
      </div>

      {loading && (
        <>
          {Array.from({ length: 5 }).map((_, index) => (
            <div className="flex gap-3" key={index}>
              <div className="hidden sm:flex flex-col gap-1 items-center">
                <div className="w-[2px] h-full bg-black rounded-lg" />
                <IconBullet />
                <div className="w-[2px] h-full bg-black rounded-lg" />
              </div>
              <div className="bg-gray-100 rounded-3xl p-4 flex flex-col gap-1 text-sm mb-4 w-full">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                <label className="flex items-center gap-1">
                  <span className="h-4 bg-gray-200 rounded animate-pulse w-24" />
                  <div className="flex gap-2 items-center">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-16" />
                    <div className="w-6 h-4 bg-gray-200 rounded-full animate-pulse" />
                  </div>
                </label>
                <div className="bg-gray-200 rounded-3xl p-4 w-full animate-pulse">
                  <div className="h-3 bg-gray-400 rounded animate-pulse mb-2" />
                  <div className="h-3 bg-gray-400 rounded animate-pulse mb-2 w-2/3" />
                  <div className="h-3 bg-gray-400 rounded animate-pulse mb-2 w-3/4" />
                </div>

                <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2 text-xs" />
              </div>
            </div>
          ))}
        </>
      )}
      {ordenarPorDataDeCriacao(
        timeline?.data?.filter((e: any) =>
          timelineItemBelongsToDepartamento(e, departamentoActive),
        ),
      )?.map((e: any) => (
        <div className="flex gap-3" key={e.id}>
          {e.type_timeline === 1 && <TimelineDesk data={e} />}
          {e.type_timeline === 2 && (
            <TimelineNotas
              data={e}
              departamentos={departamentos}
              departamentoActive={departamentoActive}
            />
          )}
          {e.type_timeline === 3 && (
            <TimelineTask
              data={e}
              departamentos={departamentos}
              departamentoActive={departamentoActive}
            />
          )}
        </div>
      ))}
    </div>
  );
}
